import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { resolveApiBaseUrl, shouldUseLocalApiOverride } from './baseUrl';

// API base URL from environment variable, defaults to localhost
const API_BASE_URL = resolveApiBaseUrl();

// Token storage keys
export const TOKEN_KEYS = {
  ACCESS_TOKEN: 'omniscribe_access_token',
  REFRESH_TOKEN: 'omniscribe_refresh_token',
} as const;

// API Error type matching backend response format
// Backend returns: { error: { code: string, message: string } }
export interface ApiErrorResponse {
  error: {
    code: string;
    message: string;
  };
}

// Legacy format for backwards compatibility
export interface ApiError {
  error: string;
  message: string;
  details?: Record<string, string>;
}

// Quota error codes that need special user-friendly messages
export const QUOTA_ERROR_CODES = {
  QUOTA_EXCEEDED: 'QUOTA_EXCEEDED',
  STORAGE_QUOTA_EXCEEDED: 'STORAGE_QUOTA_EXCEEDED',
  TRANSLATION_QUOTA_EXCEEDED: 'TRANSLATION_QUOTA_EXCEEDED',
} as const;

// HTTP status error codes for i18n
export const HTTP_ERROR_CODES = {
  401: 'UNAUTHORIZED',
  403: 'FORBIDDEN',
  404: 'NOT_FOUND',
  409: 'CONFLICT',
  429: 'TOO_MANY_REQUESTS',
  500: 'SERVER_ERROR',
  502: 'SERVER_ERROR',
  503: 'SERVER_ERROR',
  504: 'SERVER_ERROR',
} as const;

// Network error codes
export const NETWORK_ERROR_CODES = {
  ECONNABORTED: 'TIMEOUT',
  ECONNRESET: 'CONNECTION_ERROR',
  ENOTFOUND: 'CONNECTION_ERROR',
  ETIMEDOUT: 'TIMEOUT',
} as const;

// Extract error code from API error for i18n translation
export function getApiErrorCode(error: unknown): string | null {
  if (!axios.isAxiosError(error)) {
    return null;
  }

  const axiosError = error as AxiosError<ApiErrorResponse | ApiError | string>;
  let responseData = axiosError.response?.data;
  if (typeof responseData === 'string') {
    const trimmed = responseData.trim();
    if (!trimmed) {
      responseData = undefined;
    } else {
      try {
        responseData = JSON.parse(trimmed) as ApiErrorResponse | ApiError;
      } catch {
        return NETWORK_ERROR_CODES.ECONNABORTED;
      }
    }
  }

  if (responseData && typeof responseData === 'object' && 'error' in responseData) {
    const errorObj = responseData.error;
    if (typeof errorObj === 'object' && errorObj !== null && 'code' in errorObj) {
      const code = (errorObj as { code?: unknown }).code;
      if (typeof code === 'string' && code.trim()) {
        return code;
      }
    }
    if (typeof errorObj === 'string' && errorObj.trim()) {
      return errorObj;
    }
  }

  // HTTP status code
  if (axiosError.response?.status) {
    const statusCode = axiosError.response.status;
    if (HTTP_ERROR_CODES[statusCode as keyof typeof HTTP_ERROR_CODES]) {
      return HTTP_ERROR_CODES[statusCode as keyof typeof HTTP_ERROR_CODES];
    }
  }

  // Network errors
  if (axiosError.code && NETWORK_ERROR_CODES[axiosError.code as keyof typeof NETWORK_ERROR_CODES]) {
    return NETWORK_ERROR_CODES[axiosError.code as keyof typeof NETWORK_ERROR_CODES];
  }

  if (!axiosError.response) {
    return 'CONNECTION_ERROR';
  }

  return null;
}

// Extract user-friendly message from API error (for backward compatibility)
export function handleApiError(error: unknown): string {
  const code = getApiErrorCode(error);

  if (code && QUOTA_ERROR_CODES[code as keyof typeof QUOTA_ERROR_CODES]) {
    const errorMessages: Record<string, string> = {
      QUOTA_EXCEEDED: 'You have reached your transcription quota. Please upgrade your plan to continue.',
      STORAGE_QUOTA_EXCEEDED: 'You have reached your storage quota. Please upgrade your plan or delete some files.',
      TRANSLATION_QUOTA_EXCEEDED: 'You have reached your translation quota. Please upgrade your plan to continue.',
    };
    return errorMessages[code] || code;
  }

  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ApiErrorResponse | ApiError | string>;
    let responseData = axiosError.response?.data;
    if (typeof responseData === 'string') {
      const trimmed = responseData.trim();
      if (!trimmed) {
        responseData = undefined;
      } else {
        try {
          responseData = JSON.parse(trimmed) as ApiErrorResponse | ApiError;
        } catch {
          return trimmed;
        }
      }
    }

    // Handle new nested error format: { error: { code, message } }
    if (responseData && typeof responseData === 'object' && 'error' in responseData) {
      const errorObj = responseData.error;
      if (typeof errorObj === 'object' && errorObj !== null && 'message' in errorObj) {
        const message = (errorObj as { message: unknown }).message;
        if (typeof message === 'string' && message.trim()) {
          return message;
        }
      }
      // Legacy format: { error: string, message: string }
      if (typeof errorObj === 'string') {
        if ('message' in responseData && typeof (responseData as ApiError).message === 'string') {
          return (responseData as ApiError).message;
        }
        if (errorObj.trim()) {
          return errorObj;
        }
      }
    }

    if (axiosError.response?.status === 401) {
      return 'Please log in to continue';
    }
    if (axiosError.response?.status === 403) {
      return 'You do not have permission to perform this action';
    }
    if (axiosError.response?.status === 404) {
      return 'The requested resource was not found';
    }
    if (axiosError.response?.status === 409) {
      return 'This resource already exists';
    }
    if (axiosError.response?.status === 429) {
      return 'Too many requests. Please try again later';
    }
    if (axiosError.response?.status && axiosError.response.status >= 500) {
      return 'Server error. Please try again later';
    }
    if (axiosError.code === 'ECONNABORTED') {
      return 'Request timed out. Please try again';
    }
    if (!axiosError.response) {
      return 'Unable to connect to server. Please check your connection';
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

// Public axios client without auth interceptors.
export const publicApiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

function enforceLocalDevApiBaseURL(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  const localApiBaseUrl = resolveApiBaseUrl();
  if (
    shouldUseLocalApiOverride(
      config.baseURL,
      typeof window !== 'undefined' ? window.location : undefined,
      import.meta.env.DEV,
    )
  ) {
    config.baseURL = localApiBaseUrl;
  }
  return config;
}

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}> = [];

// Process queued requests after token refresh
const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else if (token) {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor: attach auth token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    config = enforceLocalDevApiBaseURL(config);
    const token = localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN);
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

publicApiClient.interceptors.request.use(enforceLocalDevApiBaseURL);

// Response interceptor: handle 401 and token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Only attempt refresh for 401 errors, not on refresh endpoint itself, and not already retried
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/refresh') &&
      !originalRequest.url?.includes('/auth/login')
    ) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return apiClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem(TOKEN_KEYS.REFRESH_TOKEN);
      if (!refreshToken) {
        // No refresh token, clear tokens and reject
        localStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN);
        localStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
        isRefreshing = false;
        processQueue(new Error('No refresh token'), null);
        return Promise.reject(error);
      }

      try {
        // Attempt to refresh the token
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refresh_token: refreshToken,
        });

        const { access_token, refresh_token: newRefreshToken } = response.data;

        // Store new tokens
        localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, access_token);
        if (newRefreshToken) {
          localStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, newRefreshToken);
        }

        // Update authorization header for original request
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
        }

        isRefreshing = false;
        processQueue(null, access_token);

        // Retry the original request
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed, clear tokens
        localStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN);
        localStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
        isRefreshing = false;
        processQueue(new Error('Token refresh failed'), null);
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
